import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';
import {
  placeSpotlightCard,
  spotlightBlockers,
  type SpotlightRect,
  type SpotlightSize,
} from '../lib/spotlight-placement';

/** What the member can press while a spotlight is up. */
export type SpotlightInteraction =
  /** Nothing on the page: the card's buttons only. */
  | 'none'
  /** Only the highlighted element. */
  | 'target'
  /** The whole page. It still dims around the element, but nothing is blocked. */
  | 'free';

const HOLE_PADDING = 6;

function sameRect(a: SpotlightRect | null, b: SpotlightRect | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function holeFor(el: Element): SpotlightRect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - HOLE_PADDING,
    left: r.left - HOLE_PADDING,
    width: r.width + HOLE_PADDING * 2,
    height: r.height + HOLE_PADDING * 2,
  };
}

/**
 * Dims the screen around one element and shows a message card beside it, the way
 * a game tutorial does.
 *
 * Everything it renders carries `data-tutorial-ui`, and `pointer-events: auto`
 * explicitly: an open modal dialog sets `pointer-events: none` on the body and
 * treats a press on anything outside itself as a reason to close. A dialog that
 * can host a spotlight passes `keepOpenForTutorial` as its `onInteractOutside`.
 *
 * The element's position is re-read every animation frame while mounted. A
 * target can move for many reasons (scrolling, images loading, a sticky bar, the
 * keyboard opening), and missing one leaves the hole over the wrong thing.
 */
export function Spotlight({
  target,
  interaction = 'none',
  children,
  className,
  label,
}: {
  /** The element to highlight. Null shows the card centred over a dimmed page. */
  target: Element | null;
  interaction?: SpotlightInteraction;
  /** The message card's contents. */
  children: React.ReactNode;
  className?: string;
  label?: string;
}) {
  const [hole, setHole] = React.useState<SpotlightRect | null>(null);
  const [viewport, setViewport] = React.useState<SpotlightSize>({ width: 0, height: 0 });
  const [card, setCard] = React.useState<SpotlightSize>({ width: 0, height: 0 });
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    let frame = 0;
    const read = () => {
      setViewport((v) =>
        v.width === window.innerWidth && v.height === window.innerHeight
          ? v
          : { width: window.innerWidth, height: window.innerHeight },
      );
      const next = target ? holeFor(target) : null;
      setHole((prev) => (sameRect(prev, next) ? prev : next));
      const c = cardRef.current;
      if (c) {
        setCard((prev) =>
          prev.width === c.offsetWidth && prev.height === c.offsetHeight
            ? prev
            : { width: c.offsetWidth, height: c.offsetHeight },
        );
      }
      frame = requestAnimationFrame(read);
    };
    read();
    return () => cancelAnimationFrame(frame);
  }, [target]);

  // Bring a new target into view, once per target.
  React.useEffect(() => {
    if (!target) return;
    const r = target.getBoundingClientRect();
    const fullyVisible = r.top >= 0 && r.bottom <= window.innerHeight;
    if (fullyVisible) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      block: r.height > window.innerHeight * 0.8 ? 'start' : 'center',
      behavior: reduced ? 'auto' : 'smooth',
    });
  }, [target]);

  const position = placeSpotlightCard(target ? hole : null, card, viewport);
  const dim = interaction === 'free' ? 'rgb(8 12 20 / 0.42)' : 'rgb(8 12 20 / 0.66)';

  return createPortal(
    <div data-tutorial-ui className="pointer-events-none fixed inset-0 z-[70] overflow-hidden">
      {target && hole ? (
        <div
          aria-hidden
          className="absolute rounded-lg outline outline-2 outline-primary"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            boxShadow: `0 0 0 100vmax ${dim}`,
          }}
        />
      ) : (
        <div aria-hidden className="absolute inset-0" style={{ background: dim }} />
      )}

      {interaction !== 'free' &&
        (interaction === 'target' && target && hole ? (
          spotlightBlockers(hole, viewport).map((b, i) => (
            <div
              key={i}
              data-tutorial-ui
              aria-hidden
              className="pointer-events-auto absolute"
              style={b}
            />
          ))
        ) : (
          <div data-tutorial-ui aria-hidden className="pointer-events-auto absolute inset-0" />
        ))}

      <div
        ref={cardRef}
        data-tutorial-ui
        role="dialog"
        aria-modal="false"
        aria-label={label}
        className={cn(
          'pointer-events-auto absolute w-[min(360px,calc(100vw-24px))] rounded-xl border bg-card p-4 text-card-foreground shadow-elevated',
          className,
        )}
        style={{
          top: position.top,
          left: position.left,
          // Measured on the first frame; hidden until then so it never flashes
          // in the top-left corner.
          visibility: card.width > 0 ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
